<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.index,
                    },
                    {
                        name: activity.title,
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="mt-3">
            <div class="md:flex justify-between">
                <div></div>
                <div>
                    <div v-if="activity.gradePill">
                        <GradePill :grade-pill="activity.gradePill" />
                    </div>
                </div>
            </div>
            <AppWellColorful class="mt-3">
                <AppBanner v-if="quiz" class="mt-3 bg-gradient-to-r from-purple-600 rounded-2xl">
                    <div class="flex flex-col items-center align-middle justify-center">
                        <div class="text-xl">{{ $t('messages.activity_completed!') }}</div>
                        <div class="text-lg mt-1 text-indigo-100">
                            {{ $t('messages.below_we_tell_you_how_it_went') }}
                        </div>
                    </div>
                </AppBanner>

                <div class="flex justify-center mt-3" v-if="activity.tag">
                    <div>
                        <span>
                            {{
                                $t('messages.you_have_tried_level_of_tag', {
                                    level: activity.difficulty,
                                    tag: activity.tag.name,
                                })
                            }}
                        </span>
                        <span
                            :class="[activity.feedback_info?.level_reached ? 'bg-green-200' : 'bg-red-200']"
                            class="ml-1 py-1 px-3 rounded-2xl"
                        >
                            <span v-if="activity.feedback_info?.level_reached">{{
                                $t('messages.practice_quiz_went_well')
                            }}</span>
                            <span v-else>{{ $t('messages.practice_quiz_not_reached') }}</span>
                        </span>
                    </div>
                </div>

                <AppBanner class="bg-gradient-to-r from-purple-600 rounded-2xl">
                    <div class="flex justify-center text-lg">
                        <span>{{ $t('messages.level_validation_status') }}</span>
                    </div>
                </AppBanner>
                <div v-if="activity.tag.expected_level" class="flex gap-2 items-center mt-3">
                    <IconTargetArrow class="text-indigo-100 w-12 h-12" />
                    <span
                        v-html="$t('messages.remember_expected_level_to_reach', { level: activity.tag.expected_level })"
                    />
                </div>

                <div class="md:grid md:grid-cols-3 md:gap-6 mt-3">
                    <AppCard>
                        <div class="mt-3 flex justify-center gap-2 items-center">
                            <GuestMedallionLevelsInfo :result="result" :tag="activity.tag" size="lg" />
                        </div>
                    </AppCard>

                    <AppCard v-if="quiz || activity.feedback_info">
                        <div v-if="quiz">
                            <div>{{ $tc('messages.questions', 2) }}</div>
                            <div class="flex flex-wrap gap-2">
                                <a
                                    v-for="(question, index) in quiz.questions"
                                    :key="question.question_id"
                                    :href="`#question_${question.question_id}`"
                                >
                                    <div class="text-center text-sm text-gray-600">
                                        {{ index + 1 }}
                                    </div>
                                    <div>
                                        <CorrectIncorrectIcon :is-correct="!!question.answers_correct_count" />
                                    </div>
                                </a>
                            </div>
                        </div>
                        <div class="mt-3" v-if="activity.feedback_info">
                            {{ activity.feedback_info.feedback }}
                        </div>
                    </AppCard>
                    <AppCard v-if="activity.feedback_info">
                        <div class="flex justify-center">
                            <img :src="activity.feedback_info.reaction_image" alt="" class="w-32 h-32" />
                        </div>
                    </AppCard>
                </div>
            </AppWellColorful>

            <AppBanner class="mt-3" v-if="quiz">
                <div class="flex justify-center text-lg">
                    <span>{{ $t('messages.your_questions_answer_details') }}</span>
                </div>
            </AppBanner>
        </div>

        <QuizReview
            :activity="activity"
            :questions="quiz.questions"
            :students="quiz.students"
            :mode="quiz.mode"
            v-if="quiz"
        />

        <div>
            <div v-if="activity.practice.retries" class="mt-5">
                <div class="w-full border-t border-gray-300" />
                <div class="md:flex gap-4 mt-5">
                    <div>
                        <div class="">{{ $t('messages.keep_practicing') }}</div>

                        <div class="flex gap-x-2 mt-5">
                            <AppButton element="InertiaLink" :href="activity.practice.subject.url" desired-action>
                                {{ activity.practice.subject.text }}
                            </AppButton>
                            <div v-for="retry in activity.practice.retries" :key="retry.url">
                                <AppButton element="InertiaLink" :href="retry.url" desired-action>
                                    {{ retry.text }}
                                </AppButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="tag" class="mt-3">
            <div>
                {{ $tc('messages.links', 2) }}
            </div>
            <div class="my-1">
                <div v-for="link in tag.links" :key="link.url">
                    <div>
                        <a :href="link.url" class="text-indigo-500">
                            {{ link.title }}
                        </a>
                    </div>
                    <div class="text-gray-700 text-sm mt-1" v-if="link.description">
                        {{ link.description }}
                    </div>
                </div>
            </div>
        </div>

        <Modal :show="showModal" @close="showModal = false" v-if="meta.advert">
            <div class="flex justify-center flex-wrap">
                {{ $t('messages.guest_practice_show_modal_advert') }}
            </div>
            <div class="mt-1 flex justify-center">
                <a :href="meta.advert.url" target="_blank" rel="noopener noreferrer">
                    <img
                        :src="meta.advert.image_path"
                        :alt="meta.advert.name"
                        class="w-full"
                        style="max-width: 500px"
                    />
                </a>
            </div>
            <div class="mt-3 flex justify-center" @click="showModal = false">
                <AppButton element="button" type="button" desired-action>
                    {{ $t('messages.see_result') }}
                </AppButton>
            </div>
        </Modal>

        <Teleport to="body">
            <div class="relative z-20 pointer-events-none">
                <div class="fixed z-10 inset-0">
                    <div class="flex items-center justify-center min-h-full p-4">
                        <ConfettiExplosion v-if="showConfetti" />
                    </div>
                </div>
            </div>
        </Teleport>
    </PanelPage>
</template>

<script>
import LandingLayout from '@/Shared/LandingLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppWell from '@/Shared/AppWell.vue';
import RubricResponseShow from '@/Pages/Teacher/Activities/Rubrics/RubricResponseShow.vue';
import QuizReview from '@/components/QuizReview.vue';
import AppButton from '@/components/AppButton.vue';
import Modal from '@/components/Modal.vue';
import GradePill from '@/Pages/Student/Activity/GradePill.vue';
import ConfettiExplosion from 'vue-confetti-explosion';
import MedallionLevelsInfo from '@/components/MedallionLevelsInfo.vue';
import AppCard from '@/Shared/AppCard.vue';
import ActivityModeIcon from '@/components/Quiz/ActivityModeIcon.vue';
import ZoomableImage from '@/components/ZoomableImage.vue';
import CorrectIncorrectIcon from '@/components/Quiz/CorrectIncorrectIcon.vue';
import BadgePill from '@/components/BadgePill.vue';
import { IconTargetArrow, IconVs } from '@tabler/icons-vue';
import DuelPartnerSearchPanel from '@/Pages/Student/Activity/Practice/Duels/partials/DuelPartnerSearchPanel.vue';
import AppBanner from '@/Shared/AppBanner.vue';
import AppWellColorful from '@/Shared/AppWellColorful.vue';
import GuestMedallionLevelsInfo from './partials/GuestMedallionLevelsInfo.vue';

export default {
    components: {
        GuestMedallionLevelsInfo,
        AppWellColorful,
        AppBanner,
        DuelPartnerSearchPanel,
        BadgePill,
        CorrectIncorrectIcon,
        ZoomableImage,
        ActivityModeIcon,
        AppCard,
        MedallionLevelsInfo,
        ConfettiExplosion,
        GradePill,
        Modal,
        AppButton,
        QuizReview,
        PanelPage,
        AppWell,
        RubricResponseShow,
        IconVs,
        IconTargetArrow,
    },
    layout: LandingLayout,
    name: 'Show',
    props: ['activity', 'quiz', 'result', 'tag', 'meta'],
    data() {
        return {
            showConfetti: false,
            showModal: false,
        };
    },
    mounted() {
        if (this.meta.advert !== null) {
            this.showModal = true;
        }

        if (this.activity.scoreModal.show_modal) {
            if (this.activity.scoreModal.is_top_score) {
                setTimeout(() => {
                    this.showConfetti = true;
                }, 1600);
            }
        }

        this.handleResult();
    },

    methods: {
        handleResult() {
            if (this.result.level_reached) {
                //
            }
        },
    },
};
</script>
